import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LifetimeStatus, SubscriptionLimit } from '../types';
import PayPalOrderButton from '../PayPalOrderButton';
import { getTierFeatures } from '../../../utils/tierFeatures';
import { usePermissions, type Feature } from '../../../contexts/PermissionContext';
import type { PermissionDisplayMetadata } from '../../../services/permissionService';
import { LifetimeTieredProgressBar } from './LifetimeTieredProgressBar';

interface LifetimeTabProps {
  lifetimeStatus: LifetimeStatus;
  onPurchaseSuccess: () => void;
  tierLimits: Map<string, SubscriptionLimit>;
  paypalClientId: string | null;
}

// Tier configuration matching backend LIFETIME_TIERS
const LIFETIME_TIERS = {
  1: { name: 'Early Bird', description: 'First 25 accounts', price: 150, discount: '50% OFF', minPurchase: 1, maxPurchase: 25 },
  2: { name: 'Standard', description: 'Accounts 26-75', price: 225, discount: '25% OFF', minPurchase: 26, maxPurchase: 75 },
  3: { name: 'Full Price', description: 'Accounts 76-100', price: 300, discount: null, minPurchase: 76, maxPurchase: 100 },
};

export default function LifetimeTab({ lifetimeStatus, onPurchaseSuccess, tierLimits, paypalClientId }: LifetimeTabProps) {
  const { getPermissionMetadata } = usePermissions();

  // Get limits for generating features - use Pro tier limits since early_supporter has same features as Pro
  const proLimits = tierLimits.get('pro');
  const freeLimits = tierLimits.get('free');
  const starterLimits = tierLimits.get('starter');

  // Generate features using the same utility as subscription cards (Pro tier features)
  const rawFeatures = getTierFeatures({
    tier: 'pro',
    limits: proLimits || null,
    getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
    freeLimits: freeLimits || null,
    starterLimits: starterLimits || null,
  });

  // For lifetime, replace "Everything in Starter, plus:" header with Auto-Resize feature
  // since that's the only Starter feature not already listed in Pro features
  const autoResizeMeta = getPermissionMetadata('auto_resize_image' as Feature);
  const features = rawFeatures.map(feature => {
    if (feature.isHeader && feature.text.includes('Starter')) {
      return {
        text: autoResizeMeta?.display_name || 'Auto-Resize Image',
        included: true,
        bold: false,
        isHeader: false,
      };
    }
    return feature;
  });

  const getTierCapacity = (tierNum: number) => {
    const tier = LIFETIME_TIERS[tierNum as keyof typeof LIFETIME_TIERS];
    return tier ? tier.maxPurchase - tier.minPurchase + 1 : 0;
  };

  // Helper to determine which tier a purchase number falls into
  const getPurchaseTier = (purchaseNum: number) => {
    if (purchaseNum <= 25) return 1;
    if (purchaseNum <= 75) return 2;
    return 3;
  };

  // If user already has lifetime, show centered ownership card with stats
  if (lifetimeStatus.user_has_lifetime) {
    const userTier = getPurchaseTier(lifetimeStatus.user_purchase_number || 1);
    const userTierInfo = LIFETIME_TIERS[userTier as keyof typeof LIFETIME_TIERS];

    return (
      <div className="flex justify-center">
        <Card className="relative w-full max-w-2xl ring-2 ring-green-500">
          <Badge className="absolute top-3 right-3 bg-green-500" variant="default">
            Active
          </Badge>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CardTitle className="text-2xl">Lifetime Access</CardTitle>
            </div>
            <CardDescription>
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <span>Forever access to all Pro features</span>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column - Features */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Your benefits:</h4>
                <ul className="space-y-2 text-sm">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {!feature.isHeader && (
                        feature.included ? (
                          <span className="text-green-500 flex-shrink-0">✓</span>
                        ) : null
                      )}
                      {feature.isHeader && <span className="h-4 w-4 flex-shrink-0" />}
                      <span className={feature.bold ? 'font-medium' : ''}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500 flex-shrink-0">✓</span>
                    <span className="text-purple-500">All future Pro features</span>
                  </li>
                </ul>
              </div>

              {/* Right column - Purchase Info */}
              <div className="flex flex-col">
                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Your purchase:</h4>
                <div className="space-y-3">
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">
                      Early Supporter #{lifetimeStatus.user_purchase_number}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {userTierInfo?.name} tier (${userTierInfo?.price})
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress - Full width */}
            <div className="mt-6 pt-4 border-t">
              <LifetimeTieredProgressBar totalSold={lifetimeStatus.total_sold} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main purchase UI - wide centered card with two columns
  return (
    <div className="flex justify-center">
      {lifetimeStatus.available && lifetimeStatus.current_tier ? (
        <Card className="relative w-full max-w-2xl ring-2 ring-purple-500">
          <Badge className="absolute top-3 right-3 bg-purple-500" variant="default">
            {LIFETIME_TIERS[lifetimeStatus.current_tier as keyof typeof LIFETIME_TIERS]?.name || 'Limited'}
          </Badge>

          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CardTitle className="text-2xl">Lifetime Access</CardTitle>
            </div>
            <CardDescription>
              One-time payment • Forever access • Limited to 100 accounts
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column - Features */}
              <div>
                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">What's included:</h4>
                <ul className="space-y-2 text-sm">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {!feature.isHeader && (
                        feature.included ? (
                          <span className="text-green-500 flex-shrink-0">✓</span>
                        ) : null
                      )}
                      {feature.isHeader && <span className="h-4 w-4 flex-shrink-0" />}
                      <span className={feature.bold ? 'font-medium' : ''}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                  <li className="flex items-center gap-2">
                    <span className="text-purple-500 flex-shrink-0">✓</span>
                    <span className="text-purple-500">All future Pro features</span>
                  </li>
                </ul>
              </div>

              {/* Right column - Price & PayPal */}
              <div className="flex flex-col">
                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {lifetimeStatus.original_price && (
                      <span className="text-2xl font-medium text-muted-foreground line-through">
                        ${lifetimeStatus.original_price}
                      </span>
                    )}
                    <span className="text-4xl font-bold">${lifetimeStatus.current_price}</span>
                    <span className="text-muted-foreground text-sm">one-time</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {lifetimeStatus.discount_percentage && (
                      <Badge variant="default" className="bg-green-500">
                        {lifetimeStatus.discount_percentage}% OFF
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {lifetimeStatus.slots_remaining} of {getTierCapacity(lifetimeStatus.current_tier)} spots left
                    </span>
                  </div>
                </div>

                {/* PayPal Button - component handles its own SDK loading */}
                <div className="w-full flex-grow" style={{ colorScheme: 'none', height: '130px', minHeight: '130px', overflow: 'hidden' }}>
                  {paypalClientId ? (
                    <PayPalOrderButton
                      containerId="paypal-lifetime-button"
                      paypalClientId={paypalClientId}
                      onSuccess={onPurchaseSuccess}
                    />
                  ) : (
                    <div className="w-full h-12 bg-muted rounded-md animate-pulse flex items-center justify-center">
                      <span className="text-sm text-muted-foreground">Loading PayPal...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress - Full width */}
            <div className="mt-4 pt-4 border-t">
              <LifetimeTieredProgressBar totalSold={lifetimeStatus.total_sold} />
              {lifetimeStatus.current_tier < 3 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Price increases to ${lifetimeStatus.current_tier === 1 ? '224.99' : '299.99'} after {lifetimeStatus.slots_remaining} more purchases
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Sold Out Card */
        <Card className="relative w-full max-w-md opacity-75">
          <Badge className="absolute top-3 right-3" variant="secondary">
            Sold Out
          </Badge>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-2xl">Lifetime Access</CardTitle>
            </div>
            <CardDescription>
              All 100 lifetime spots have been claimed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Thank you for your interest! Consider subscribing to our Pro plan for the same great features.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
